import { BaseView, PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { GameViewController } from "../controllers/GameViewController";
import { GameModel } from "../models/game/GameModel";
import { GamefieldView } from "./components/gamefield/GamefieldView";
import { DeckView } from "./components/deck/DeckView";
import { GameUIResourcesPackage } from "../../preloader/resources/GameUIResourcesPackage";
import { CellViewFactory } from "./components/cells/CellViewFactory";
import { TriangleViewFactory } from "./components/triangles/TriangleViewFactory";
import { FigureViewFactory } from "./components/figures/FigureViewFactory";
import { FullscreenBackground } from "../../common/components/FullscreenBackground";
import { InputHandler } from "./components/InputHandler";
import { IDestroyOptions } from "@pixi/display";
import { ScoreView } from "./components/score/ScoreView";
import { PlayerModel } from "../models/player/PlayerModel";
import { Localization } from "../../common/Localization";
import { AudioManager } from "../../../audio/AudioManager";
import { AudioNames } from "../../../audio/AudioNames";
import { AdTimer } from "./components/AdTimer";
import { ScaleUpIconButton } from "../../common/components/buttons/ScaleUpIconButton";

export class GameView extends BaseView<void> {
    private gamefieldView: GamefieldView;
    private deckView: DeckView;
    private inputHandler: InputHandler;

    private scoreView: ScoreView;
    private settingsButton: ScaleUpIconButton;
    private infoButton: ScaleUpIconButton;
    private adTimer: AdTimer;

    public constructor(
        private readonly gameUIResourcesPackage: GameUIResourcesPackage,
        private readonly gameModel: GameModel,
        private readonly playerModel: PlayerModel,
        private readonly gameViewController: GameViewController,
        private readonly cellViewFactory: CellViewFactory,
        private readonly triangleViewFactory: TriangleViewFactory,
        private readonly figureViewFactory: FigureViewFactory,
        private readonly canvasScaler: ICanvasScaler,
        private readonly fullscreenBackground: FullscreenBackground,
        private readonly localization: Localization,
        private readonly audioManager: AudioManager,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(operationControls);
        this.build();
        this.layout();
        this.subscribe();
    }

    public start(): void {
        this.audioManager.play(AudioNames.MainMusic);
    }

    public build(): void {
        const backgroundTexture = window.maxTextureSize <= 4096
            ? this.gameUIResourcesPackage.assets_game_background_game_mobile_png :
            this.gameUIResourcesPackage.assets_game_background_png;
        this.fullscreenBackground.updateImage(backgroundTexture);
        this.gamefieldView = new GamefieldView(
            this.gameUIResourcesPackage,
            this.cellViewFactory,
            this.triangleViewFactory,
            this.figureViewFactory,
            this.playerModel,
            this.gameModel,
            this.gameViewController,
            this.localization,
            this.audioManager
        );
        this.deckView = new DeckView(
            this.gameUIResourcesPackage,
            this.figureViewFactory,
            this.gameModel,
            this.playerModel,
            this.gameViewController,
            this.audioManager
        );
        this.adTimer = new AdTimer(this.gameViewController);
        this.inputHandler = new InputHandler(this.gameViewController, this.deckView, this.gamefieldView, this.canvasScaler, this.audioManager);
        this.scoreView = new ScoreView(this.gameUIResourcesPackage.assets_game_counter_substrate_png, this.gameModel, this.playerModel);
        this.infoButton = new ScaleUpIconButton({ defaultTexture: this.gameUIResourcesPackage.assets_game_button_substrate_purpure_png, iconOptions: { icon: this.gameUIResourcesPackage.assets_game_button_icon_info_png, positionOption: { x: 35.5, y: 20 } } }, this.audioManager);
        this.settingsButton = new ScaleUpIconButton({ defaultTexture: this.gameUIResourcesPackage.assets_game_button_substrate_purpure_png, iconOptions: { icon: this.gameUIResourcesPackage.assets_game_button_icon_settings_png, positionOption: { x: 27, y: 17 } } }, this.audioManager);
        this.addChild(this.gamefieldView, this.deckView, this.adTimer, this.infoButton, this.settingsButton, this.scoreView, this.inputHandler);
        if (this.playerModel.IsFirstLaunch) {
            this.gameViewController.setFirstLaunch();
            this.gameViewController.showInfoPopup();
        }
    }

    private subscribe(): void {
        this.infoButton.onUp.add(this.handleInfoButtonClick, this);
        this.settingsButton.onUp.add(this.handleSettingsButtonClick, this);
    }

    private unsubscribe(): void {
        this.infoButton.onUp.remove(this.handleInfoButtonClick, this);
        this.settingsButton.onUp.remove(this.handleSettingsButtonClick, this);
    }

    private handleInfoButtonClick(): void {
        this.gameViewController.showInfoPopup();
    }

    private handleSettingsButtonClick(): void {
        this.gameViewController.showSettingsPopup();
    }

    private layout(): void {
        const referenceWidth = this.canvasScaler.referenceWidth;
        this.gamefieldView.position.set((referenceWidth - this.gamefieldView.width) * 0.5, 175);
        this.deckView.position.set((referenceWidth - this.deckView.width) * 0.5, 1500);
        this.inputHandler.position.set((referenceWidth - this.inputHandler.width) * 0.5, 0);
        this.scoreView.position.set((referenceWidth - this.scoreView.width) * 0.5, 50);
        this.infoButton.position.set(85, 40);
        this.settingsButton.position.set(860, 40);
        this.adTimer.position.set(150, this.deckView.position.y + 210);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.unsubscribe();
        super.destroy(options);
    }
}